/**
 * Workshop Equipment Hooks (#2323)
 *
 * Non-lift workshop devices — tyre machine, wheel balancer, emissions
 * analyser. Lifts live in `useLifts`; these are a plain register with no
 * capacity, dimensions or maintenance state.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export interface WorkshopEquipment {
  id: string;
  name: string;
  identifier: string;
  location: string | null;
  description: string | null;
  isActive: boolean;
  /** Sender addresses used to tag inbound tester reports (PF-136). */
  senderEmails: string[];
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface WorkshopEquipmentFilters {
  [key: string]: string | number | boolean | undefined;
  q?: string;
  isActive?: boolean;
  sortBy?: 'name' | 'identifier';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

export interface CreateWorkshopEquipmentData {
  name: string;
  identifier: string;
  location?: string | null;
  description?: string | null;
  isActive?: boolean;
  senderEmails?: string[];
}

export type UpdateWorkshopEquipmentData = Partial<CreateWorkshopEquipmentData>;

/**
 * Hook to fetch paginated list of workshop equipment with infinite scroll
 */
export function useWorkshopEquipmentList(filters: WorkshopEquipmentFilters = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.workshopEquipment.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<WorkshopEquipment>>('/workshop-equipment', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to fetch a single piece of workshop equipment by ID
 */
export function useWorkshopEquipmentItem(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workshopEquipment.detail(id ?? ''),
    queryFn: () => api.get<WorkshopEquipment>(`/workshop-equipment/${id}`),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to create a new piece of workshop equipment
 */
export function useCreateWorkshopEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWorkshopEquipmentData) =>
      api.post<WorkshopEquipment>('/workshop-equipment', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workshopEquipment.lists(),
      });
    },
  });
}

/**
 * Hook to update an existing piece of workshop equipment
 */
export function useUpdateWorkshopEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkshopEquipmentData }) =>
      api.patch<WorkshopEquipment>(`/workshop-equipment/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workshopEquipment.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workshopEquipment.lists(),
      });
    },
  });
}

/**
 * Hook to delete (soft delete) a piece of workshop equipment
 */
export function useDeleteWorkshopEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/workshop-equipment/${id}`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workshopEquipment.detail(id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.workshopEquipment.lists(),
      });
    },
  });
}
